import React, { useEffect, useRef, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { Col, Row, Alert, Button } from 'reactstrap';
import { jwtDecode } from 'jwt-decode';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { actions } from 'modules/admin';

const UserMerge = props => {
  const [isCSOrAdmin, setIsCSOrAdmin] = useState(false);
  const [email1, setEmail1] = useState('');
  const [email2, setEmail2] = useState('');
  const [user1, setUser1] = useState(null);
  const [user2, setUser2] = useState(null);
  const [survivingRecord, setSurvivingRecord] = useState('first');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [mergeComplete, setMergeComplete] = useState(false);
  const [notCompleteMessage, setNotCompleteMessage] = useState('');
  const [error, setError] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  const store = useStore();
  const dispatch = useDispatch();

  const user = store.getState().user;

  useEffect(() => {
    if (!isCSOrAdmin) {
      if (user.isAuthenticated && user.authToken) {
        const decodedToken = jwtDecode(user.authToken);
        if (decodedToken.role === 'cs' || decodedToken.role === 'admin') {
          setIsCSOrAdmin(true);
        }
      }
    }
  }, []);

  const retrieveUserID = user => {
    return user?.id || null;
  };

  const searchUser = async (email, setUserFn) => {
    setIsSearching(true);
    setError(null);

    try {
      const resp = await dispatch(actions.searchByEmail(email));

      if (resp.totalRows === 1) {
        const userRecord = resp.rows[0];
        setUserFn(userRecord);
        setIsSearching(false);
        return userRecord;
      } else {
        // need an exact match
        setIsSearching(false);
        return null;
      }
    } catch (error) {
      setError(`Error finding user with email ${email}: ${error.message}`);
      setIsSearching(false);
      return null;
    }
  };

  // Handle email input changes and search for users
  const handleEmailChange = async (email, setEmailFn, setUserFn) => {
    setEmailFn(email);
    setUserFn(null); // Clear existing user when email changes
  };

  // Handle lookup for a specific email
  const handleLookupUser = async (email, setUserFn) => {
    if (!email) {
      setError('Email is required for lookup');
      return;
    }

    await searchUser(email, setUserFn);
  };

  const handleMergeSubmit = async e => {
    e.preventDefault();

    if (!email1 || !email2) {
      setError('Both email fields are required');
      return;
    }

    if (email1 === email2) {
      setError('Emails must be different');
      return;
    }

    // Clear any previous errors
    setError(null);

    // Search for users if not already found
    let userObj1 = user1;
    let userObj2 = user2;

    if (!userObj1) {
      userObj1 = await searchUser(email1, setUser1);
      if (!userObj1) return; // Error already set in searchUser
    }

    if (!userObj2) {
      userObj2 = await searchUser(email2, setUser2);
      if (!userObj2) return; // Error already set in searchUser
    }

    const userId1 = retrieveUserID(userObj1);
    const userId2 = retrieveUserID(userObj2);

    if (!userId1 || !userId2) {
      setError('Could not determine user IDs for one or both emails');
      return;
    }

    setIsProcessing(true);
    setMergeComplete(false);

    /* Determine which email will be kept based on user selection
     * This is mildly confusing - the surviving user email is
     * currently associated with the user ID that will not be usable */
    const sourceUserId = survivingRecord === 'first' ? userId2 : userId1;
    const survivingUserId = survivingRecord === 'first' ? userId1 : userId2;
    const emailToCheck = survivingRecord === 'first' ? email2 : email1;
    const survivingEmail = survivingRecord === 'first' ? email1 : email2;

    try {
      /* CHECK if MERGED-<surviving email>-MERGED already exists
       * if it does - error out and force team to contact tech.
       * There is a manual step involved for cases where a surviving email
       * has been merged already. */
      const alreadyMergedRecordSearch = await dispatch(actions.searchByEmail(`MERGED-${survivingEmail}-MERGED`));

      if (alreadyMergedRecordSearch.totalRows > 0) {
        throw { message: 'Surviving record already has an existing merge. Please contact tech for support.' };
      }

      // Dispatch the merge action (assuming this returns a Promise)
      await dispatch(actions.mergeUsers(survivingUserId, sourceUserId));

      // Start polling to check if source email has been removed from database
      startPolling();
    } catch (error) {
      setError(`Error initiating merge: ${error.message}`);
      setIsProcessing(false);
    }
  };

  // Check merge status (can be triggered by polling or manual check)
  const checkMergeStatus = async () => {
    try {
      // Check old record to ensure the email on it is "MERGED"
      const userIDToCheck = survivingRecord === 'first' ? user1.id : user2.id;
      const exists = await dispatch(actions.lookUpUser(userIDToCheck));

      if (exists.email.match(/^MERGED/)) {
        // Email no longer exists, merge is complete
        clearInterval(pollingInterval);
        setPollingInterval(null);
        setIsProcessing(false);
        setMergeComplete(true);
      } else {
        setNotCompleteMessage('Merge not yet complete.');
      }
    } catch (error) {
      setError(`Error checking merge status: ${error.message}`);
      // Don't stop polling on error, as it might be temporary
    }
  };

  // Start polling to check merge status
  const startPolling = () => {
    // Clear any existing polling interval
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    // Set up a new polling interval
    const intervalId = setInterval(() => {
      checkMergeStatus();
    }, 15000); // Check every 15 seconds

    setPollingInterval(intervalId);
  };

  // Manual check button handler
  const handleManualCheck = () => {
    const emailToCheck = survivingRecord === 'first' ? email2 : email1;
    checkMergeStatus();
  };

  // Clean up polling interval when component unmounts
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Reset the form for a new merge
  const handleReset = () => {
    setEmail1('');
    setEmail2('');
    setUser1(null);
    setUser2(null);
    setSurvivingRecord('first');
    setIsProcessing(false);
    setMergeComplete(false);
    setError(null);

    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const UserInfo = ({ user, userId }) => {
    if (!user) return null;

    return (
      <div className="mt-1 text-sm bg-gray-50 p-2 rounded">
        <p>
          <span className="font-semibold">User ID:</span> {userId || 'Unknown'}
        </p>
        {/* Display other user info as needed */}
        {user.firstName && (
          <p>
            <span className="font-semibold">First Name:</span> {user.firstName}
          </p>
        )}
        {user.lastName && (
          <p>
            <span className="font-semibold">Last Name:</span> {user.lastName}
          </p>
        )}
      </div>
    );
  };

  return !isCSOrAdmin ? (
    <div>
      <Alert color="danger">Please log in - customer service.</Alert>
    </div>
  ) : (
    <div className="email-merge-container p-4 border rounded">
      <h2 className="text-xl font-bold mb-4">Email Merge Tool</h2>

      {mergeComplete ? (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          <p>Merge completed successfully!</p>
          <Button
            onClick={handleReset}
            className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            color="primary"
          >
            Start New Merge
          </Button>
        </div>
      ) : (
        <form onSubmit={handleMergeSubmit}>
          {error && <Alert color="danger">{error}</Alert>}

          <Row className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Email 1:
              <div className="flex mt-1">
                <input
                  type="email"
                  value={email1}
                  onChange={e => handleEmailChange(e.target.value, setEmail1, setUser1)}
                  disabled={isProcessing}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="user@example.com"
                />
                <Button
                  type="button"
                  onClick={() => handleLookupUser(email1, setUser1)}
                  disabled={isProcessing || isSearching || !email1}
                  className="ml-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  color="secondary"
                >
                  Lookup
                </Button>
              </div>
              <UserInfo user={user1} userId={retrieveUserID(user1)} />
            </label>
          </Row>

          <Row className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Email 2:
              <div className="flex mt-1">
                <input
                  type="email"
                  value={email2}
                  onChange={e => handleEmailChange(e.target.value, setEmail2, setUser2)}
                  disabled={isProcessing}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="user@example.com"
                />
                <Button
                  type="button"
                  onClick={() => handleLookupUser(email2, setUser2)}
                  disabled={isProcessing || isSearching || !email2}
                  className="ml-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  color="secondary"
                >
                  Lookup
                </Button>
              </div>
              <UserInfo user={user2} userId={retrieveUserID(user2)} />
            </label>
          </Row>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Which email should survive the merge?</label>
            <div className="mt-2">
              <label className="inline-flex items-center mr-4">
                <input
                  type="radio"
                  value="first"
                  checked={survivingRecord === 'first'}
                  onChange={() => setSurvivingRecord('first')}
                  disabled={isProcessing}
                  className="form-radio h-5 w-5 text-blue-600"
                />
                <span className="ml-2">Email 1</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="second"
                  checked={survivingRecord === 'second'}
                  onChange={() => setSurvivingRecord('second')}
                  disabled={isProcessing}
                  className="form-radio h-5 w-5 text-blue-600"
                />
                <span className="ml-2">Email 2</span>
              </label>
            </div>
          </div>

          {isSearching && (
            <div className="flex items-center mb-4">
              <div className="w-4 h-4 border-t-2 border-r-2 border-blue-500 rounded-full animate-spin mr-2"></div>
              <span>Searching for user...</span>
            </div>
          )}

          {isProcessing ? (
            <div className="mb-4">
              <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
                <p className="font-bold">Merge in progress...</p>
                <p className="text-sm">
                  {survivingRecord === 'first'
                    ? `Merging ${email2} into ${email1}`
                    : `Merging ${email1} into ${email2}`}
                </p>
                <div className="flex items-center mt-2">
                  <div className="w-4 h-4 border-t-2 border-r-2 border-blue-500 rounded-full animate-spin mr-2"></div>
                  <span>Auto-checking every 15 seconds</span>
                </div>
                {notCompleteMessage && <Alert>{notCompleteMessage}</Alert>}
                <Button
                  type="button"
                  onClick={handleManualCheck}
                  className="mt-3 bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-4 rounded text-sm"
                  color="primary"
                >
                  Check Now
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <Button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                color="primary"
                disabled={!email1 || !email2 || email1 === email2 || !user1 || !user2 || isSearching}
              >
                Merge Emails
              </Button>
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default UserMerge;
